import React, { useState } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Shield,
  UserCheck,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronLeft,
  FileText,
  TrendingUp,
  Mail,
  Crown,
} from 'lucide-react';


import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  permission?: string;
}

/**
 * Admin Layout Component
 * Layout cho toàn bộ admin pages với sidebar navigation
 */
const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isAdmin, canManageUsers, canApproveTeachers } = usePermissions();

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const navItems: NavItem[] = [
    {
      path: '/admin',
      label: 'Tổng quan',
      icon: <LayoutDashboard className="w-5 h-5" />,
    },
    {
      path: '/admin/users',
      label: 'Quản lý thành viên',
      icon: <Users className="w-5 h-5" />,
      permission: 'users:read',
    },

    {
      path: '/admin/teachers',
      label: 'Duyệt giáo viên',
      icon: <UserCheck className="w-5 h-5" />,
      permission: 'teachers:approve',
    },
    {
      path: '/admin/membership',
      label: 'Gói PRO & Điểm',
      icon: <Crown className="w-5 h-5" />,
    },
    {
      path: '/admin/statistics',
      label: 'Xuất thống kê',
      icon: <FileText className="w-5 h-5" />,
      permission: 'reports:read',
    },
    {
      path: '/admin/access_stats',
      label: 'Access Stats',
      icon: <TrendingUp className="w-5 h-5" />,
      permission: 'reports:read',
    },
    {
      path: '/admin/settings',
      label: 'Cài đặt',
      icon: <Settings className="w-5 h-5" />,
      permission: 'system:manage',
    },
    {
      path: '/admin/resources',
      label: 'Quản lý Tài liệu',
      icon: <FileText className="w-5 h-5" />,
      permission: 'system:manage',
    },
    {
      path: '/admin/email',
      label: 'Email Marketing',
      icon: <Mail className="w-5 h-5" />,
      permission: 'system:manage',
    },

  ];

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter(item => {
    if (!item.permission) return true;
    const caps: string[] = Array.isArray(user?.permissions)
      ? user.permissions
      : (user?.permissions as any)?.capabilities || [];
    return caps.includes(item.permission);
  });

  const handleLogout = async () => {
    if (confirm('Bạn có chắc muốn đăng xuất?')) {
      await logout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-600 hover:text-gray-900"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <h1 className="text-lg font-bold text-gray-900">Admin Dashboard</h1>
          <div className="w-6" /> {/* Spacer */}
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-screen transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${sidebarOpen ? 'w-64' : 'lg:w-20'}
        `}
      >
        <div className="h-full bg-gradient-to-b from-blue-600 to-blue-800 text-white flex flex-col">
          {/* Logo/Header */}
          <div className="p-6 border-b border-blue-500">
            <div className="flex items-center justify-between">
              {sidebarOpen && (
                <div>
                  <h2 className="text-xl font-bold">Admin</h2>
                  <p className="text-sm text-blue-200 mt-1">Quản trị hệ thống</p>
                </div>
              )}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:block text-white hover:bg-blue-700 p-2 rounded-lg transition"
              >
                {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-blue-500">
            <div className="flex items-center space-x-3">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-10 h-10 rounded-full border-2 border-white"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center border-2 border-white">
                  <span className="text-sm font-bold">{user?.name[0]}</span>
                </div>
              )}
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-blue-200 truncate">{user?.email}</p>
                  <span className="inline-block px-2 py-0.5 mt-1 text-xs bg-yellow-400 text-yellow-900 rounded">
                    Admin
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center px-6 py-3 transition-colors
                    ${isActive
                      ? 'bg-blue-700 text-white border-l-4 border-white'
                      : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                    }
                    ${!sidebarOpen && 'justify-center px-4'}
                  `}
                  title={!sidebarOpen ? item.label : undefined}
                >
                  {item.icon}
                  {sidebarOpen && (
                    <span className="ml-3 font-medium">{item.label}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Back to Site + Logout */}
          <div className="border-t border-blue-500 p-4 space-y-2">
            <Link
              to="/"
              className={`
                flex items-center px-4 py-2 text-blue-100 hover:bg-blue-700 hover:text-white rounded-lg transition
                ${!sidebarOpen && 'justify-center px-2'}
              `}
              title={!sidebarOpen ? 'Về trang chủ' : undefined}
            >
              <ChevronLeft className="w-5 h-5" />
              {sidebarOpen && <span className="ml-2">Về trang chủ</span>}
            </Link>
            <button
              onClick={handleLogout}
              className={`
                w-full flex items-center px-4 py-2 text-red-200 hover:bg-red-600 hover:text-white rounded-lg transition
                ${!sidebarOpen && 'justify-center px-2'}
              `}
              title={!sidebarOpen ? 'Đăng xuất' : undefined}
            >
              <LogOut className="w-5 h-5" />
              {sidebarOpen && <span className="ml-2">Đăng xuất</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`
          transition-all duration-300 pt-16 lg:pt-0
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        `}
      >
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
